// profile.ts — lens-profile reuse for a capture (ARCHITECTURE.md §14). Looks
// the device fingerprint up on the server, turns a usable profile into the
// WASM session's prior (`prior_intrinsics` normalized — the session scales
// them to its frame — and `prior_sigma` with c_px rescaled to the tracking
// frame) and words the Capture chip.
// Pure helpers are split from the one networked call so they unit-test
// without a browser; lookup failures degrade to plain self-calibration.

import { devicesApi } from '../api';
import type { DeviceProfile, DeviceRegistration, LensSigma, NormIntrinsics } from '../api';
import { browserEnv, collectFingerprint } from './fingerprint';
import type { DeviceFingerprint, TrackLike } from './fingerprint';

export interface DeviceInfo {
  fingerprint: DeviceFingerprint;
  profile: DeviceProfile | null; // null: no profile yet (or lookup failed)
  usable: boolean; // profile has measured intrinsics to start from
  mismatch: boolean; // the session dropped the prior (profile_mismatch)
}

export interface PriorConfig {
  prior_intrinsics: NormIntrinsics;
  prior_sigma: LensSigma;
}

// usableProfile: a profile whose intrinsics were actually measured.
export function usableProfile(p: DeviceProfile | null): p is DeviceProfile & { intrinsics: NonNullable<DeviceProfile['intrinsics']> } {
  return !!p && !!p.intrinsics && p.n > 0 && p.intrinsics.fx > 0 && p.intrinsics.fy > 0;
}

// priorConfig builds the session prior for a width×height tracking frame.
// The intrinsics stay normalized (wasm/session scales them); c_px is moved
// from profile pixels to frame pixels. A missing sigma falls back to a weak
// 5% focal prior.
export function priorConfig(p: DeviceProfile, width: number, height: number): PriorConfig | null {
  if (!usableProfile(p) || width <= 0 || height <= 0) return null;
  const n = p.intrinsics;
  const s = p.sigma;
  const scale = p.width > 0 ? width / p.width : 1;
  return {
    prior_intrinsics: { fx: n.fx, fy: n.fy, cx: n.cx, cy: n.cy, k1: n.k1, k2: n.k2, p1: n.p1, p2: n.p2 },
    prior_sigma: {
      f_rel: s && s.f_rel > 0 ? Math.min(s.f_rel, 0.45) : 0.05, // session rejects ≥ 0.5
      c_px: s && s.c_px > 0 ? s.c_px * scale : 0.05 * width,
      k1: Math.max(0, s?.k1 ?? 0),
      k2: Math.max(0, s?.k2 ?? 0),
    },
  };
}

// hfovDeg is the horizontal field of view of normalized focal fx/W.
export function hfovDeg(fxNorm: number): number {
  return fxNorm > 0 ? (2 * Math.atan(0.5 / fxNorm) * 180) / Math.PI : 0;
}

export function chipText(info: DeviceInfo): string {
  const p = info.profile;
  if (!usableProfile(p)) return 'Calibrating new device';
  const sig = p.sigma && p.sigma.f_rel > 0 ? `, ±${(p.sigma.f_rel * 100).toFixed(1)}%` : '';
  return `Lens profile: ${p.label || info.fingerprint.label} (${p.n} scan${p.n === 1 ? '' : 's'}${sig})`;
}

export function registration(info: DeviceInfo): DeviceRegistration {
  const fp = info.fingerprint;
  return { device_key: fp.key, device_label: fp.label, fingerprint: fp.fingerprint, intrinsics_source: info.usable ? 'profile' : 'selfcal' };
}

// identifyDevice fingerprints the live camera and looks its profile up. It
// never throws: any failure yields "calibrating new device".
export async function identifyDevice(track: TrackLike | null, frameW: number, frameH: number): Promise<DeviceInfo> {
  const fingerprint = await collectFingerprint(browserEnv(track, frameW, frameH));
  let profile: DeviceProfile | null = null;
  try {
    profile = await devicesApi.find(fingerprint.key);
  } catch (err) {
    console.warn('[openscan/profile] profile lookup failed; self-calibrating', err);
  }
  const usable = usableProfile(profile);
  console.info(
    `[openscan/profile] ${profile ? `profile n=${profile.n} locked=${profile.locked} usable=${usable}` : 'no profile'} for fp=${fingerprint.fingerprint.slice(0, 12)}…`,
  );
  return { fingerprint, profile, usable, mismatch: false };
}
